import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

// Module-level deduplication (survives Strict Mode remounts and double-mounts)
// Emergent session_id tokens are SINGLE-USE — second call returns 401.
const processedSessionIds = new Set();
let inFlight = null;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AuthCallback() {
    const navigate = useNavigate();
    const { setUser } = useAuth();
    const [error, setError] = useState(null);

    useEffect(() => {
        const hash = window.location.hash || "";
        const match = hash.match(/session_id=([^&]+)/);
        if (!match) {
            navigate("/", { replace: true });
            return;
        }
        const sessionId = match[1];

        // If a request for THIS session_id is already in flight, wait for it.
        if (inFlight && inFlight.sessionId === sessionId) {
            inFlight.promise
                .then((data) => {
                    setUser(data);
                    window.history.replaceState(null, "", window.location.pathname);
                    navigate("/dashboard", { replace: true });
                })
                .catch(() => navigate("/", { replace: true }));
            return;
        }

        // If we already processed this session_id, just navigate.
        if (processedSessionIds.has(sessionId)) {
            window.history.replaceState(null, "", window.location.pathname);
            navigate("/dashboard", { replace: true });
            return;
        }

        const promise = api
            .post("/auth/session", { session_id: sessionId })
            .then(({ data }) => {
                processedSessionIds.add(sessionId);
                return data;
            });
        inFlight = { sessionId, promise };

        promise
            .then((data) => {
                inFlight = null;
                setUser(data);
                window.history.replaceState(null, "", window.location.pathname);
                navigate("/dashboard", { replace: true });
            })
            .catch((e) => {
                inFlight = null;
                const message =
                    e?.response?.data?.detail ||
                    "No pudimos verificar tu sesión. Probá iniciar sesión de nuevo.";
                toast.error(message);
                setError(message);
                // Clean hash so a retry doesn't loop on the same expired session_id
                window.history.replaceState(null, "", window.location.pathname);
                setTimeout(() => navigate("/", { replace: true }), 1200);
            });
    }, [navigate, setUser]);

    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-white gap-4 px-6" data-testid="auth-callback-page">
            {error ? (
                <>
                    <div className="h-12 w-12 rounded-full bg-[#FEE2E2] flex items-center justify-center">
                        <span className="text-[#991B1B] text-xl font-bold">!</span>
                    </div>
                    <p className="text-sm text-[#991B1B] text-center max-w-sm" data-testid="auth-error">{error}</p>
                </>
            ) : (
                <>
                    <div className="h-12 w-12 rounded-full border-2 border-[#064E3B] border-t-transparent animate-spin" />
                    <p className="text-sm uppercase tracking-widest text-[#64748B]">Verificando sesión...</p>
                </>
            )}
        </div>
    );
}
